// Download hand-off — the one screen where a phone belongs on the page, because the phone
// IS the instruction. The QR is the same magic link that's in their inbox, so scanning it
// signs them straight in. Store badges drop into the two slots at build (env URLs).
import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import type { Answers } from '../lib/session';
import { useIsNarrow } from '../lib/useIsNarrow';
import { card } from '../components/ui';
import { StoreBadges } from '../components/StoreBadges';
import { beginCheckout } from '../lib/checkout';
import { ApiError, RateLimited } from '../lib/api';

const TRIAL_DAYS = Number(import.meta.env.VITE_TRIAL_DAYS || 7);

// Optional, quiet, and honest: the trial is card-free by design (decision:
// card-free lean, product/07a), so this never blocks the download hand-off.
// It exists for the patient who would rather not think about billing again.
// The server enforces eligibility; a refusal renders as calm copy, not an error.
function SubscribeOffer({ trialDays }: { trialDays: number }) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);
  if (hidden) {
    return notice ? (
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 18, lineHeight: 1.5 }}>{notice}</div>
    ) : null;
  }
  const go = async () => {
    setBusy(true);
    setNotice(null);
    try {
      await beginCheckout();
    } catch (e) {
      if (e instanceof RateLimited) {
        setNotice('Too many attempts from this connection. Give it a few minutes and try again.');
      } else if (e instanceof ApiError && (e.status === 403 || e.status === 409)) {
        // Region not launched, or the account is covered. Both mean: nothing to pay.
        setNotice('There is nothing to pay on your account right now. Your access continues as is.');
        setHidden(true);
      } else {
        setNotice('We could not start checkout. Your free trial is unaffected. Please try again.');
      }
      setBusy(false);
    }
  };
  return (
    <div style={{ ...card, borderRadius: 14, padding: '16px 18px', marginTop: 22 }}>
      <div style={{ fontSize: 14, fontWeight: 800 }}>Want billing off your mind?</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 4, lineHeight: 1.5 }}>
        Your first {trialDays} days are free either way. Set up your subscription now and your plan continues without interruption. Cancel anytime.
      </div>
      <button
        type="button"
        onClick={go}
        disabled={busy}
        style={{
          marginTop: 12,
          background: 'var(--teal)',
          color: '#fff',
          fontSize: 14,
          fontWeight: 800,
          padding: '11px 20px',
          borderRadius: 10,
          border: 'none',
          cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.7 : 1,
        }}
      >
        {busy ? 'Opening secure checkout…' : 'Set up subscription'}
      </button>
      {notice && (
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 10, lineHeight: 1.5 }}>{notice}</div>
      )}
    </div>
  );
}
export function GetTheApp({
  answers,
  claimLink,
  offerSubscription = false,
}: {
  answers: Answers;
  claimLink?: string;
  offerSubscription?: boolean;
}) {
  const narrow = useIsNarrow();
  const first = ((answers.email as string) || '').split('@')[0] || 'there';
  const goal = (answers.goal as string) || null;
  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)' }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: narrow ? '28px 20px 48px' : '56px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 320px', gap: 28, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--teal-deep)', textTransform: 'uppercase', letterSpacing: '.14em' }}>You’re in</div>
            <div className="pretty" style={{ fontSize: narrow ? 30 : 38, fontWeight: 800, lineHeight: 1.12, marginTop: 10 }}>Day 1 is on your phone.</div>
            <div className="pretty" style={{ fontSize: 16, fontWeight: 500, color: 'var(--slate)', marginTop: 12, lineHeight: 1.55 }}>
              Your {TRIAL_DAYS} days start when you open the app. A few minutes a day, and your streak begins.
            </div>

            {/* QR = the account-claim magic link (no PHI). Scanning it on a phone opens the
                app/claim flow signed in. Falls back to a "check your email" prompt if the
                invite link didn't come back (e.g. delivery issue — the emailed link still works). */}
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 24 }}>
              <div style={{ width: 116, height: 116, borderRadius: 14, background: '#fff', boxShadow: 'var(--card-shadow)', padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {claimLink ? (
                  <QRCodeSVG value={claimLink} size={92} level="M" aria-label="Sign-in QR code" />
                ) : (
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate)', textAlign: 'center', lineHeight: 1.4 }}>
                    Check your email for the sign-in link
                  </div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800 }}>Scan to sign in</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 4, maxWidth: 200, lineHeight: 1.5 }}>
                  Point your camera here. No password to type. It’s the same link that’s in your inbox.
                </div>
              </div>
            </div>

            <StoreBadges style={{ marginTop: 22 }} />

            {offerSubscription && <SubscribeOffer trialDays={TRIAL_DAYS} />}
          </div>

          {/* Phone — the one place a device mockup belongs */}
          <div style={{ ...card, borderRadius: 28, padding: 16, background: '#fff', justifySelf: narrow ? 'center' : 'end' }}>
            <div style={{ borderRadius: 18, background: 'var(--surface)', padding: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate)' }}>Morning, {first}</div>
              <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>Day 1 of 84</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 2 }}>Your first win is a few minutes away</div>
              <div style={{ ...card, borderRadius: 12, padding: 14, marginTop: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--teal-deep)', letterSpacing: '.1em' }}>TODAY’S SESSION</div>
                {['Quad sets', 'Heel slides', 'Hip flexor stretch'].map((t) => (
                  <div key={t} style={{ fontSize: 13, fontWeight: 700, marginTop: 8 }}>{t}</div>
                ))}
              </div>
              {goal && (
                <div style={{ background: 'var(--teal-tint)', borderRadius: 10, padding: '10px 12px', marginTop: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--teal-deep)', letterSpacing: '.08em' }}>WHY YOU’RE DOING THIS</div>
                  <div style={{ fontSize: 12, fontWeight: 700, marginTop: 3 }}>{goal}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
